import React, { useState, useEffect, useRef } from 'react';
import { useSignup } from './SignupProvider';

const ManagerInfoStep: React.FC = () => {
  const { state, updateManagerData, setError, clearError, nextStep, previousStep } = useSignup();
  const { manager } = state.data;
  const { errors } = state;
  const displayNameInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    displayName: manager?.displayName || '',
    email: manager?.email || '',
    phoneNumber: manager?.phoneNumber || '',
    gmailAddress: manager?.gmailAddress || '',
  });

  // Update form data when manager data changes
  useEffect(() => {
    if (manager) {
      setFormData({
        displayName: manager.displayName || '',
        email: manager.email || '',
        phoneNumber: manager.phoneNumber || '',
        gmailAddress: manager.gmailAddress || '',
      });
    }
  }, [manager]);

  // Auto-focus on the first input field when component mounts
  useEffect(() => {
    if (displayNameInputRef.current) {
      displayNameInputRef.current.focus();
    }
  }, []);

  const formatPhoneNumber = (value: string, previousValue: string = '') => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // If user is deleting (new value has fewer digits than before), allow it
    const previousDigits = previousValue.replace(/\D/g, '');
    const isDeleting = phoneNumber.length < previousDigits.length;
    
    // Limit to 10 digits
    const limitedPhoneNumber = phoneNumber.slice(0, 10);
    
    // If deleting and we're at a formatting boundary, don't reformat immediately
    if (isDeleting && limitedPhoneNumber.length > 0) {
      // Allow partial formatting during deletion
      if (limitedPhoneNumber.length <= 3) {
        return limitedPhoneNumber;
      } else if (limitedPhoneNumber.length <= 6) {
        return `(${limitedPhoneNumber.slice(0, 3)}) ${limitedPhoneNumber.slice(3)}`;
      }
    }
    
    // Format as (XXX) XXX-XXXX
    if (limitedPhoneNumber.length >= 6) {
      return `(${limitedPhoneNumber.slice(0, 3)}) ${limitedPhoneNumber.slice(3, 6)}-${limitedPhoneNumber.slice(6, 10)}`;
    } else if (limitedPhoneNumber.length >= 3) {
      return `(${limitedPhoneNumber.slice(0, 3)}) ${limitedPhoneNumber.slice(3)}`;
    } else {
      return limitedPhoneNumber;
    }
  };

  const handleInputChange = (field: string, value: string) => {
    let processedValue = value;
    
    // Format phone number
    if (field === 'phoneNumber') {
      processedValue = formatPhoneNumber(value, formData.phoneNumber);
    }
    
    // Normalize Gmail address to lowercase for consistency
    if (field === 'gmailAddress') {
      processedValue = value.toLowerCase();
    }
    
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    updateManagerData({ [field]: processedValue });
    
    // Clear error when user starts typing
    if (errors[field]) {
      clearError(field);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Manager admin name is required';
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }

    // Email validation (optional field - only validate format if provided)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && formData.email.trim() && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Gmail address validation (optional but must be valid Gmail if provided)
    if (formData.gmailAddress) {
      const gmailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/i;
      if (!gmailRegex.test(formData.gmailAddress)) {
        newErrors.gmailAddress = 'Please enter a valid Gmail address (must end with @gmail.com)';
      }
    }

    // Phone validation (10-digit US phone number for SMS)
    const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
    if (formData.phoneNumber && phoneDigits.length !== 10) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit US phone number for SMS verification';
    } else if (formData.phoneNumber) {
      // Additional validation for US phone numbers (area codes can't start with 0 or 1)
      const areaCode = phoneDigits.substring(0, 3);
      if (areaCode.startsWith('0') || areaCode.startsWith('1')) {
        newErrors.phoneNumber = 'Please enter a valid US phone number (area code cannot start with 0 or 1)';
      }
    }

    // Set errors
    Object.entries(newErrors).forEach(([field, message]) => {
      setError(field, message);
    });

    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      nextStep();
    }
  };

  const handlePrevious = () => {
    previousStep();
  };

  return (
    <div className="manager-info-step">
      <div className="step-header">
        <h2>Manager Admin Details</h2>
        <p>Set up the primary manager admin account for your restaurant</p>
      </div>

      <form className="manager-form" onSubmit={(e) => e.preventDefault()}>
        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="displayName">Manager Admin Name *</label>
            <input
              ref={displayNameInputRef}
              type="text"
              id="displayName"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              className={errors.displayName ? 'error' : ''}
              placeholder="Enter manager admin's full name"
            />
            {errors.displayName && <span className="error-message">{errors.displayName}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Support Contact Email</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={errors.email ? 'error' : ''}
              placeholder="manager.admin@restaurant.com"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">
              Cell Phone * <span className="helper-text">(Console Cell Login)</span>
            </label>
            <input
              type="tel"
              id="phoneNumber"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              className={errors.phoneNumber ? 'error' : ''}
              placeholder="(555) 123-4567"
            />
            {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="gmailAddress">
              Gmail Address <span className="helper-text">(Console Gmail Login)</span>
            </label>
            <input
              type="email"
              id="gmailAddress"
              value={formData.gmailAddress}
              onChange={(e) => handleInputChange('gmailAddress', e.target.value)}
              className={errors.gmailAddress ? 'error' : ''}
              placeholder="manager@gmail.com"
            />
            {errors.gmailAddress && <span className="error-message">{errors.gmailAddress}</span>}
          </div>
        </div>

        <div className="manager-info-note">
          <div className="info-icon">ℹ️</div>
          <div className="info-content">
            <h4>Manager Admin Account</h4>
            <p>This person will be the primary administrator with full access to:</p>
            <ul>
              <li>Staff management and permissions</li>
              <li>Restaurant settings and configuration</li>
              <li>Analytics and reporting</li>
              <li>Billing and account management</li>
            </ul>
          </div>
        </div>
      </form>

      <div className="step-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handlePrevious}
        >
          Back: Restaurant Info
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNext}
        >
          Next: Pricing
        </button>
      </div>

      <style>{`
        .manager-info-step {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }

        .step-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .step-header h2 {
          color: #2196F3;
          margin-bottom: 8px;
          font-size: 2rem;
          font-weight: 600;
        }

        .step-header p {
          color: #6c757d;
          font-size: 1.1rem;
        }

        .manager-form {
          margin-bottom: 32px;
        }

        .form-row {
          display: flex;
          gap: 16px;
          margin-bottom: 20px;
        }

        .form-group {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .form-group.full-width {
          width: 100%;
        }

        .form-group label {
          margin-bottom: 6px;
          font-weight: 500;
          color: #495057;
        }

        .form-group input {
          padding: 12px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .form-group input:focus {
          outline: none;
          border-color: #2196F3;
        }

        .form-group input.error {
          border-color: #dc3545;
        }

        .error-message {
          color: #dc3545;
          font-size: 0.875rem;
          margin-top: 4px;
        }

        .field-help {
          color: #6c757d;
          font-size: 0.875rem;
          margin-top: 4px;
          font-style: italic;
        }

        .helper-text {
          color: #999;
          font-size: 0.8rem;
          font-style: italic;
          font-weight: normal;
          margin-left: 8px;
        }

        .manager-info-note {
          display: flex;
          gap: 12px;
          background: #f8f9fa;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
          border-left: 4px solid #2196F3;
        }

        .info-icon {
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .info-content h4 {
          margin: 0 0 8px 0;
          color: #2196F3;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .info-content p {
          margin: 0 0 8px 0;
          color: #495057;
        }

        .info-content ul {
          margin: 0;
          padding-left: 20px;
          color: #6c757d;
        }

        .info-content li {
          margin-bottom: 4px;
        }

        .step-actions {
          display: flex;
          justify-content: center;
          gap: 16px;
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          flex: 1;
        }

        .btn-primary {
          background: #2196F3;
          color: white;
        }

        .btn-primary:hover {
          background: #1976D2;
          transform: translateY(-1px);
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background: #5a6268;
          transform: translateY(-1px);
        }

        @media (max-width: 768px) {
          .form-row {
            flex-direction: column;
            gap: 12px;
          }

          .manager-info-step {
            padding: 16px;
          }

          .step-header h2 {
            font-size: 1.5rem;
          }

          .step-actions {
            flex-direction: column;
            justify-content: center;
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
};

export default ManagerInfoStep;
